import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import '../common/AlertGraph/GraphStyle.css';
import PropTypes from 'prop-types';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import { OperatorTypeDetails, OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import BaseTemperatureChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BaseTemperatureChannel/BaseTemperatureChannel';
import {
  convertCelsiusToFahrenheit,
  convertDeltaCelsiusToFahrenheit,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/conversions/valueConversions';
import AppTypes from '@avtechsoftware/ra-javascript-utilities/src/types/AppTypes';
import RaaExtremeTempFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/RaaExtremeTempFaultChannel';
import DeltaTemperatureChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/DeltaTemperatureChannel/DeltaTemperatureChannel';
import D3ChannelGraphingUtil from './D3ChannelGraphingUtil';
import D3DrawThresholdOverlay from './D3DrawThresholdOverlay';
import D3Utility from './D3Utility';

/* istanbul ignore next */
function SingleLineGraph({
  data,
  temperatureScale,
  threshold,
  startTime,
  endTime,
  timeZone,
  channelKeyObject,
  customUnits,
  width,
  height,
}) {
  const channel = ChannelService
    .getChannelGroupChannelByAppType(
      channelKeyObject.channelGroupType,
      channelKeyObject.channelIndex,
      channelKeyObject.channelType,
      AppTypes.CLOUD,
    );
  let operatorType = Object.keys(OperatorTypeDetails).find(
    (type) => OperatorTypeDetails[type].symbol === threshold.operator,
  );
  const graphUtilities = D3ChannelGraphingUtil.getUtilitySetByChannel(channel);
  if (channel instanceof RaaExtremeTempFaultChannel) {
    operatorType = OperatorTypes.EQUAL_TO_BOOLEAN;
  }
  const drawThresholdOverlay = D3DrawThresholdOverlay[operatorType];

  const getPopupDisplay = (timestamp, value) => `${D3Utility
    .getTimeZoneDate(timeZone, timestamp)}  | ${channel
    .getDisplayValueWithUnits(value, { scale: temperatureScale, customUnits })}`;

  const graphRef = useRef(null);
  useEffect(() => {
    d3.select(graphRef.current).html('');
    const marginTop = 20;
    const marginRight = 20;
    const marginBottom = 30;
    const marginLeft = graphUtilities.getMarginLeft({
      trueDisplayValue: channel.trueDisplayValue,
      falseDisplayValue: channel.falseDisplayValue,
    });

    const svg = d3
      .select(graphRef.current)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMaxYMid');

    const xAxis = graphUtilities
      .getXAxis(startTime, endTime + 21600, width, marginLeft, marginRight);
    graphUtilities.drawXAxis(svg, xAxis, height, marginTop, marginBottom, timeZone);

    const yAxis = graphUtilities.getYAxis(data, height, marginBottom, marginBottom, {
      temperatureScale,
      trueDisplayValue: channel.trueDisplayValue,
      falseDisplayValue: channel.falseDisplayValue,
      thresholdValue1: threshold.value,
      thresholdValue2: threshold.value2,
    });

    graphUtilities.drawYAxis(svg, yAxis, marginLeft);
    graphUtilities.drawYAxisGrid(svg, yAxis, width, marginLeft, marginRight);

    const line = graphUtilities.getDataLine(xAxis, yAxis, {
      temperatureScale,
      trueDisplayValue: channel.trueDisplayValue,
      falseDisplayValue: channel.falseDisplayValue,
    });

    if (channel instanceof DeltaTemperatureChannel && temperatureScale === 'F') {
      threshold = {
        ...threshold,
        value: convertDeltaCelsiusToFahrenheit(threshold.value),
        value2: convertDeltaCelsiusToFahrenheit(threshold.value2),
      };
    } else if (channel instanceof BaseTemperatureChannel && temperatureScale === 'F') {
      threshold = {
        ...threshold,
        value: convertCelsiusToFahrenheit(threshold.value),
        value2: convertCelsiusToFahrenheit(threshold.value2),
      };
    }

    const overlayWidth = width - marginRight - marginLeft;
    drawThresholdOverlay(svg, threshold, height, marginLeft, overlayWidth, marginBottom, yAxis, {
      trueDisplayValue: channel.trueDisplayValue,
      falseDisplayValue: channel.falseDisplayValue,
    });
    if (data.length > 0) {
      svg
        .append('path')
        .datum(data)
        .attr('class', 'line')
        .attr('d', line)
        .style('stroke', '#EE3524')
        .style('stroke-width', '2px')
        .style('curser', 'pointer')
        .attr('fill', 'none');
      const div = d3.select(graphRef.current).append('div')
        .attr('class', 'popup')
        .style('opacity', 0);
      graphUtilities
        .drawMouseoverPoints(svg, data, xAxis, yAxis, div, marginLeft, marginTop, getPopupDisplay, {
          temperatureScale,
          trueDisplayValue: channel.trueDisplayValue,
          falseDisplayValue: channel.falseDisplayValue,
        });
    } else {
      svg
        .append('rect')
        .attr('x', marginLeft)
        .attr('y', marginTop)
        .attr('width', overlayWidth)
        .attr('height', height - marginTop - marginBottom)
        .attr('fill', 'gray')
        .attr('class', 'overlay')
        .style('opacity', '0.15')
        .text('No Data');
    }
  }, [data, width, height]);
  return <div id="Graph" ref={graphRef} />;
}

SingleLineGraph.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  temperatureScale: PropTypes.string.isRequired,
  threshold: PropTypes.shape({
    value: PropTypes.number.isRequired,
    value2: PropTypes.number,
    operator: PropTypes.string.isRequired,
  }).isRequired,
  startTime: PropTypes.number.isRequired,
  endTime: PropTypes.number.isRequired,
  timeZone: PropTypes.string.isRequired,
  channelKeyObject: PropTypes.instanceOf(ChannelKey).isRequired,
  customUnits: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
};
export default SingleLineGraph;
